import { forwardRef, memo, useLayoutEffect } from 'react';
import { useTextareaAutoResize } from '@/hooks/useTextareaAutoResize';

interface ChatTextareaProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onPaste?: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  maxHeight?: number;
  className?: string;
  enterKeyHint?: React.TextareaHTMLAttributes<HTMLTextAreaElement>['enterKeyHint'];
  autoComplete?: string;
  selectionRef?: React.MutableRefObject<{ start: number; end: number }>;
}

export const ChatTextarea = memo(
  forwardRef<HTMLTextAreaElement, ChatTextareaProps>(function ChatTextarea(props, forwardedRef) {
    const { ref, handleChange } = useTextareaAutoResize(forwardedRef, props.value, props.onChange, props.maxHeight ?? 200);

    const handleSelect = props.selectionRef
      ? () => {
          const el = ref.current;
          if (el && props.selectionRef) {
            props.selectionRef.current = { start: el.selectionStart, end: el.selectionEnd };
          }
        }
      : undefined;

    useLayoutEffect(() => {
      const el = ref.current;
      const sel = props.selectionRef?.current;
      if (!el || !sel || !props.autoFocus) return;
      el.setSelectionRange(sel.start, sel.end);
    }, []);

    return (
      <textarea
        ref={ref}
        value={props.value}
        onChange={handleChange}
        onKeyDown={props.onKeyDown}
        onPaste={props.onPaste}
        onSelect={handleSelect}
        placeholder={props.placeholder ?? 'Message...'}
        rows={1}
        disabled={props.disabled}
        autoFocus={props.autoFocus}
        enterKeyHint={props.enterKeyHint}
        autoComplete={props.autoComplete}
        className={props.className ?? "min-h-[52px] w-full resize-none bg-transparent px-4 py-3.5 text-[14px] text-zinc-100 placeholder:text-zinc-600 focus:outline-none disabled:cursor-not-allowed"}
      />
    );
  }),
);
